import { useState } from 'react';
import { GripVertical, MoreVertical, Pin } from 'lucide-react';
import { StatusBadge, getStatusVariant, getStatusLabel } from './StatusBadge';
import { TicketRowActionMenu } from './TicketRowActionMenu';
import { useLongPress } from '../../hooks/useLongPress';
import type { Ticket } from '../../types';

interface TicketRowItemProps {
  ticket: Ticket;
  onClick: () => void;
  extraBadges?: Array<{
    label: string;
    variant?: Parameters<typeof StatusBadge>[0]['variant'];
  }>;
  /** Optional pin/unpin callbacks. When omitted, the ⋮ menu is suppressed
   *  (used by list contexts that don't support pinning, e.g. legacy callers). */
  onPin?: () => void;
  onUnpin?: () => void;
  /** Disables pin/unpin while a mutation is in flight (avoids double-tap). */
  pinning?: boolean;
  /** Drag-handle bindings supplied by the parent's SortableContext. When
   *  present AND the ticket is pinned, the handle ≡ icon is rendered. */
  dragHandleProps?: {
    attributes?: Record<string, unknown>;
    listeners?: Record<string, unknown>;
    setNodeRef?: (node: HTMLElement | null) => void;
  };
}

export function TicketRowItem({
  ticket,
  onClick,
  extraBadges,
  onPin,
  onUnpin,
  pinning = false,
  dragHandleProps,
}: TicketRowItemProps) {
  /** Display name only (no Telegram @username in this row) */
  const fullName =
    [ticket.firstName, ticket.lastName].filter(Boolean).join(' ').trim() ||
    ticket.firstName ||
    'Unknown User';

  const timeAgo = formatRelativeTime(ticket.lastMessageAt || ticket.createdAt);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPinned = ticket.isPinned === true;
  const canPin = typeof onPin === 'function' && typeof onUnpin === 'function';

  // Long-press anywhere on the row opens the action menu (parity with the
  // ⋮ button). Skipped when no pin callbacks are supplied.
  const longPress = useLongPress({
    onLongPress: () => {
      if (canPin) setMenuOpen(true);
    },
  });

  // Suppress the row's onClick when a long-press just fired (the menu
  // is now open; tapping again to close is the expected gesture, NOT
  // navigation into the ticket).
  const handleClick = () => {
    if (longPress.wasLongPressTriggeredRef.current) {
      longPress.wasLongPressTriggeredRef.current = false;
      return;
    }
    onClick();
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        onPointerDown={longPress.onPointerDown}
        onPointerMove={longPress.onPointerMove}
        onPointerUp={longPress.onPointerUp}
        onPointerCancel={longPress.onPointerCancel}
        onPointerLeave={longPress.onPointerLeave}
        // Conditional side-padding reserves room for the absolute-positioned
        // ≡ (left, only when pinned + draggable) and ⋮ (right, whenever pin
        // callbacks are supplied). Without these, the icons overlap the
        // status badges on the right edge / the 📌+TICK-ID column on the left.
        className={`w-full bg-white border border-black/10 rounded-[8.75px] py-[10.667px] flex items-center gap-2.5 text-left hover:bg-surface-secondary transition touch-active ${
          isPinned && dragHandleProps ? 'pl-9' : 'pl-[10.667px]'
        } ${canPin ? 'pr-9' : 'pr-[10.667px]'}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            {/* Pinned indicator — shows only when this tech has pinned the row.
             *  Left of the ticket-ID badge so it answers "is this pinned?" at
             *  a glance without competing with the right-side status badges. */}
            {isPinned && (
              <Pin
                className="mt-[2px] h-3 w-3 shrink-0 text-text-brand"
                strokeWidth={2.5}
                aria-label="Pinned"
              />
            )}
            <span
              className="text-xs font-medium text-text-secondary px-1.5 py-0.5 rounded-md shrink-0"
              title={ticket.ticketId}
            >
              {ticket.ticketId}
            </span>

            <div className="flex flex-col gap-1.5 min-w-0">
              <span className="text-sm font-semibold text-[#0a0a0a] truncate" title={fullName}>
                {fullName}
              </span>
              <span className="text-[10.5px] leading-[14px] text-[#717182] truncate">
                {ticket.phoneNumber ? `${formatPhoneForRow(ticket.phoneNumber)} · ` : ''}
                {timeAgo}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          <StatusBadge
            label={getStatusLabel(ticket.status)}
            variant={getStatusVariant(ticket.status)}
          />
          {extraBadges?.map((b, idx) => (
            <StatusBadge
              key={`${b.label}-${idx}`}
              label={b.label}
              variant={b.variant}
            />
          ))}
          {ticket.hasUnreadMessages && (
            <StatusBadge label="Need A Live Agent" variant="brand" />
          )}
        </div>
      </button>

      {/* Drag handle — only rendered for pinned rows that have drag props
       *  attached (parent's SortableContext supplies them). Long-press on
       *  THIS element starts the drag; long-press elsewhere on the row
       *  opens the menu. iOS-style ≡ icon. Positioned absolutely on the
       *  LEFT inside the row's reserved `pl-9` padding so it sits as the
       *  visual "grab handle" before 📌 + TICK-ID, and the right edge
       *  stays clear for the status badges + ⋮ menu. */}
      {isPinned && dragHandleProps && (
        <button
          type="button"
          ref={(node) => dragHandleProps.setNodeRef?.(node as unknown as HTMLElement)}
          {...(dragHandleProps.attributes ?? {})}
          {...(dragHandleProps.listeners ?? {})}
          aria-label="Drag to reorder pin"
          // Stop click bubbling so a tap on the handle doesn't navigate
          onClick={(e) => e.stopPropagation()}
          className="absolute left-1 top-1/2 -translate-y-1/2 h-9 w-6 flex items-center justify-center text-text-tertiary touch-none cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="h-4 w-4" strokeWidth={2} aria-hidden />
        </button>
      )}

      {/* ⋮ menu button — always shown when pin callbacks are supplied.
       *  Tap opens the action menu; doesn't navigate into the ticket. */}
      {canPin && (
        <button
          type="button"
          aria-label="Ticket actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((prev) => !prev);
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-7 flex items-center justify-center text-text-tertiary hover:text-text-secondary"
        >
          <MoreVertical className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
        </button>
      )}

      {/* Menu — anchored to the row container; visible state lives here. */}
      {menuOpen && canPin && (
        <TicketRowActionMenu
          isPinned={isPinned}
          pinning={pinning}
          onPin={() => onPin?.()}
          onUnpin={() => onUnpin?.()}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
}

function formatPhoneForRow(raw: string): string {
  // DB stores digits only (e.g. 855967777515). UI wants "(+855) 96 7777 515".
  const digits = String(raw).replace(/[^\d]/g, '');
  if (digits.length <= 3) return digits;
  const country = digits.slice(0, 3);
  const rest = digits.slice(3);

  // Cambodia-style grouping (most common in this app): (+855) 96 7777 515
  if (country === '855') {
    if (rest.length === 9) {
      return `(+855) ${rest.slice(0, 2)} ${rest.slice(2, 6)} ${rest.slice(6)}`;
    }
    if (rest.length === 8) {
      return `(+855) ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
    }
  }

  // Generic fallback: "(+CCC) rest"
  return `(+${country}) ${rest}`;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
